import { useEffect, useState } from 'react';

import { useBrowserStorage } from '@aspendental/shared-utils/react';
import { useRouter } from 'next/router';

import { FACILITY_CODE_STORAGE_KEY } from '@/constants';
import { useAppContext } from '@/context';

/* * useFacilityCode hook
 * @param serverFacilityCode - facility code passed from server side props
 * 1. If facility code is passed in the URL, use that to fetch the facility details
 * 2. If 1. failed, and facility code passed from server side props, use that to fetch the facility details
 * 3. if 1. and 2. both failed, and facility code is stored in local storage, use that to fetch the facility details
 * Final fallback: use the geolocation to fetch the facility details
 * console.log('in use facilities')
 * new set office btn is introduced in google maps based on feature flag
 * if enabled, set as office btn feature in google maps when used sets the local storage for that facility
 *
 * @returns facilityCode - facility code from URL or local storage or server side props
 * @returns setFacilityCode - function to set facility code in local storage
 * @returns removeFacilityCode - function to remove facility code from local storage
 */
export function useFacilityCode(serverFacilityCode?: string | null) {
	const appConfig = useAppContext();
	const { config } = appConfig;
	const useSessionStorage = config?.featureFlags?.useSessionStorageFacilityCode;
	const showSetOfficeButtonInGoogleMapsCards = config?.featureFlags?.showSetOfficeButtonInGoogleMapsCards ?? false;

	const {
		storageValue: storedFacilityCode,
		setValue,
		removeItem,
	} = useBrowserStorage({
		key: FACILITY_CODE_STORAGE_KEY,
		storage: useSessionStorage ? globalThis.sessionStorage : globalThis.localStorage,
	});

	const [tempFacilityCode, setTempFacilityCode] = useState<string>('');

	const storageFacilityCode = storedFacilityCode?.toString();

	const router = useRouter();
	const facilityCodeFromURL = router.isReady
		? new URL(`${globalThis?.location?.origin || 'http://localhost'}${router.asPath}`).searchParams.get('fc')
		: null;

	// update tempFacilityCode based on URL, server, and storage values after render to prevent hydration issues
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			const tempFacilityCode1 = showSetOfficeButtonInGoogleMapsCards
				? // when showSetOfficeButtonInGoogleMapsCards is true use storageFacilityCode before facilityCode
				  facilityCodeFromURL ?? storageFacilityCode ?? serverFacilityCode ?? ''
				: // when showSetOfficeButtonInGoogleMapsCards is false use facilityCode before storageFacilityCode
				  facilityCodeFromURL ?? serverFacilityCode ?? storageFacilityCode ?? '';
			setTempFacilityCode(tempFacilityCode1);
		}, 0);
		return () => clearTimeout(timeoutId);
	}, [facilityCodeFromURL, serverFacilityCode, storageFacilityCode, showSetOfficeButtonInGoogleMapsCards]);

	return {
		facilityCode: tempFacilityCode,
		setFacilityCode: setValue,
		removeFacilityCode: removeItem,
	};
}
